import React, {
   useCallback,
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ro from "date-fns/locale/ro";
import { ReactSVG } from "react-svg";
import { useDispatch } from "react-redux";

import addIcon from "../../assets/svg/add.svg";
import arrowIcon from "../../assets/svg/arrow.svg";
import trashIcon from "../../assets/svg/trash.svg";

import * as ReservationsAPI from "../../api/reservationsService";
import { fetchUserInfo } from "../../api/authService";
import { UserContext } from "../../UserContext";
import { fetchUserReservations } from "../../store/reservationsSlice";
import AlertPills from "../Utils/AlertPills";
import { closePopup as closePopupStore } from "../Common/popupUIStore";
import {
   MOLDOVA_TZ,
   buildLocalMatchIso,
   busyLocalKeyFromStored,
   calendarDayStr,
   readRawDateTimeParts,
   startTimesForAutoAssign,
   wallClockDayFromIso,
   wallClockSlotKeyFromIso,
} from "../../utils/moldovaTime";
import {
   isLessonBookingWindowOpen,
   LESSON_BOOKING_WINDOW_BLOCKED_MESSAGE,
   LESSON_BOOKING_WINDOW_FULL_LABEL,
} from "../../utils/studentBookingWindow";
import { useLessonBookingWindow } from "../../hooks/useStudentBookingWindow";
import {
   availabilitySlotKey,
   buildAvailabilitySlotMap,
   dayHasAnyFreeSlot,
   dominantInstructorIdFromReservations,
   getRelevantFreeCount,
   hasEligibleInstructors,
   isAvailabilitySlotFree,
   MANDATORY_POLIGON_LESSONS,
   normalizeGearboxLabel,
   partitionSlotsByAvailability,
   remainingPoligonLessonsNeeded,
   sectorRequiresPoligon,
} from "../../utils/autoAssignAvailability";
import { setDesiredInstructor } from "../../api/usersService";
import { getBookingSettings } from "../../api/settingsService";
import useDistricts, { reloadDistricts } from "../../hooks/useDistricts";
import {
   activeDistrictNames,
   activePolygonNames,
   districtKey,
} from "../../utils/districts";

const POLYGON_CHOICE_MESSAGE = "Alege poligonul pe care vei face primele lecții.";

registerLocale("ro", ro);

const MAX_TOTAL_LESSONS = 30;

const oreDisponibile = [
   { eticheta: "07:00", oraStart: "07:00" },
   { eticheta: "08:30", oraStart: "08:30" },
   { eticheta: "10:00", oraStart: "10:00" },
   { eticheta: "11:30", oraStart: "11:30" },
   { eticheta: "13:30", oraStart: "13:30" },
   { eticheta: "15:00", oraStart: "15:00" },
   { eticheta: "16:30", oraStart: "16:30" },
   { eticheta: "18:00", oraStart: "18:00" },
];

const SLOT_LABELS = oreDisponibile.map((o) => o.oraStart);

const SERVER_ERROR_ISO_RE =
   /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?/g;
const SERVER_ERROR_LOOSE_ISO_RE =
   /\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?/g;
const SERVER_ERROR_INDEX_RE = /(?:index|slot)\s*[:=]?\s*(\d+)/gi;

function localDateStrTZ(date, tz = MOLDOVA_TZ) {
   const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
   });
   const parts = fmt.formatToParts(date);
   const day = parts.find((p) => p.type === "day")?.value ?? "01";
   const month = parts.find((p) => p.type === "month")?.value ?? "01";
   const year = parts.find((p) => p.type === "year")?.value ?? "1970";
   return `${year}-${month}-${day}`;
}

function isPastOrTodayInMoldova(date) {
   const d = localDateStrTZ(date, MOLDOVA_TZ);
   const today = localDateStrTZ(new Date(), MOLDOVA_TZ);
   return d <= today;
}

/** Candidate ISO for availability = wall-clock digits (same as /availability). */
function buildIsoForLocalDayTime(dayLocal, timeHHMM) {
   return buildLocalMatchIso(dayLocal, timeHHMM);
}

const addDays = (d, n) => {
   const x = new Date(d);
   x.setDate(x.getDate() + n);
   return x;
};

const addMonths = (d, n) => {
   const x = new Date(d);
   x.setMonth(x.getMonth() + n);
   return x;
};

const asStr = (v) => (v == null ? "" : String(v));
const asBool = (v) => {
   if (typeof v === "boolean") return v;
   if (typeof v === "number") return v === 1;
   if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "true" || s === "1" || s === "yes" || s === "da";
   }
   return false;
};

const readMedicalDocumentsFlag = (src) => {
   const candidates = [
      src?.medical_documents,
      src?.medicalDocuments,
      src?.extras?.medical_documents,
      src?.profile?.medical_documents,
      src?.user?.medical_documents,
   ];
   return candidates.some((v) => asBool(v));
};

function toPositiveIntOrNull(v) {
   const n = Number(v);
   return Number.isInteger(n) && n > 0 ? n : null;
}

function isAllowedReservationCheckpoint(totalReservations) {
   const count = Math.max(0, Number(totalReservations) || 0);
   return count === 0 || count === 15;
}

function getReservationCheckpointBlockMessage(totalReservations) {
   const count = Math.max(0, Number(totalReservations) || 0);
   const noun = count === 1 ? "rezervare existentă" : "rezervări existente";
   return `Ai ${count} ${noun}. Poți continua doar când ai exact 0 sau 15 rezervări existente. Numărul actual este nepotrivit, deci rezervările sunt blocate. Contactează un manager pentru mai multe detalii.`;
}

function getStartFromReservation(r) {
   return (
      r?.startTime ??
      r?.startDate ??
      r?.start ??
      r?.reservation?.startTime ??
      r?.reservation?.startDate ??
      null
   );
}

const capRO = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const formatDateRO = (iso) => {
   const raw = readRawDateTimeParts(iso);
   if (raw) {
      const d = new Date(
         Number(raw.year),
         Number(raw.month) - 1,
         Number(raw.day),
         12,
         0,
         0,
         0,
      );
      const str = d.toLocaleDateString("ro-RO", {
         day: "2-digit",
         month: "short",
         year: "numeric",
      });
      return capRO(str);
   }
   const d = new Date(iso);
   const str = d.toLocaleDateString("ro-RO", {
      timeZone: MOLDOVA_TZ,
      day: "2-digit",
      month: "short",
      year: "numeric",
   });
   return capRO(str);
};

const formatTimeRO = (iso) => {
   const raw = readRawDateTimeParts(iso);
   if (raw) return `${raw.hour}:${raw.minute}`;
   const d = new Date(iso);
   return d.toLocaleTimeString("ro-RO", {
      timeZone: MOLDOVA_TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
   });
};

function makeKey(iso, i) {
   return `${iso}::${i}`;
}

function normalizeIsoOrNull(value) {
   if (value == null) return null;
   try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
   } catch {
      return null;
   }
}

function normalizeIsoTokenFromServerError(raw) {
   const s = String(raw || "").trim();
   if (!s) return "";
   return s.includes("T") ? s : s.replace(" ", "T");
}

function parseEmbeddedJsonFromString(value) {
   const text = String(value || "");
   const start = text.indexOf("{");
   const end = text.lastIndexOf("}");
   if (start < 0 || end <= start) return null;
   try {
      return JSON.parse(text.slice(start, end + 1));
   } catch {
      return null;
   }
}

function collectErrorTextParts(errorLike) {
   const out = [];
   const seenObj = new Set();

   const pushText = (value) => {
      if (typeof value !== "string") return;
      const text = value.trim();
      if (!text) return;
      out.push(text);
   };

   const walk = (value, depth = 0) => {
      if (depth > 4 || value == null) return;
      if (typeof value === "string") {
         pushText(value);
         const embedded = parseEmbeddedJsonFromString(value);
         if (embedded) walk(embedded, depth + 1);
         return;
      }
      if (typeof value === "number" || typeof value === "boolean") {
         pushText(String(value));
         return;
      }
      if (Array.isArray(value)) {
         for (const x of value) walk(x, depth + 1);
         return;
      }
      if (typeof value === "object") {
         if (seenObj.has(value)) return;
         seenObj.add(value);
         for (const k of [
            "message",
            "error",
            "errors",
            "detail",
            "details",
            "reservationErrors",
            "reservations",
            "data",
            "response",
            "body",
            "payload",
         ]) {
            if (k in value) walk(value[k], depth + 1);
         }
      }
   };

   walk(errorLike);
   return Array.from(new Set(out));
}

function extractReservationErrors(errorLike) {
   const sources = [errorLike, errorLike?.payload, errorLike?.message];
   for (const src of sources) {
      if (!src) continue;
      if (Array.isArray(src?.reservationErrors)) return src.reservationErrors;
      if (typeof src === "string") {
         const embedded = parseEmbeddedJsonFromString(src);
         if (Array.isArray(embedded?.reservationErrors)) {
            return embedded.reservationErrors;
         }
      }
      if (typeof src === "object" && Array.isArray(src.reservationErrors)) {
         return src.reservationErrors;
      }
   }
   return [];
}

function extractServerConflictSelectedDates(errorLike, selectedIsoDates = []) {
   const reservationErrors = extractReservationErrors(errorLike);
   if (reservationErrors.length) {
      const out = [];
      for (const item of reservationErrors) {
         const fromField = normalizeIsoOrNull(item?.startTime);
         if (fromField) {
            out.push(fromField);
            continue;
         }
         const idx = Number(item?.index);
         if (
            Number.isInteger(idx) &&
            idx >= 0 &&
            idx < selectedIsoDates.length
         ) {
            const iso = normalizeIsoOrNull(selectedIsoDates[idx]);
            if (iso) out.push(iso);
         }
      }
      if (out.length) return Array.from(new Set(out));
   }

   const texts = collectErrorTextParts(errorLike);
   if (
      !texts.length ||
      !Array.isArray(selectedIsoDates) ||
      !selectedIsoDates.length
   ) {
      return [];
   }

   const conflictIsoSet = new Set();
   const conflictIndexSet = new Set();
   for (const text of texts) {
      const strictMatches = text.match(SERVER_ERROR_ISO_RE) || [];
      const looseMatches = text.match(SERVER_ERROR_LOOSE_ISO_RE) || [];
      for (const rawIso of [...strictMatches, ...looseMatches]) {
         const cleanedIsoToken = normalizeIsoTokenFromServerError(rawIso);
         const iso = normalizeIsoOrNull(cleanedIsoToken);
         if (iso) conflictIsoSet.add(iso);
      }

      SERVER_ERROR_INDEX_RE.lastIndex = 0;
      let indexMatch = SERVER_ERROR_INDEX_RE.exec(text);
      while (indexMatch) {
         const indexRaw = Number(indexMatch[1]);
         if (Number.isInteger(indexRaw) && indexRaw >= 0) {
            conflictIndexSet.add(indexRaw);
         }
         indexMatch = SERVER_ERROR_INDEX_RE.exec(text);
      }
   }

   for (const idx of conflictIndexSet) {
      if (idx < selectedIsoDates.length) {
         const iso = normalizeIsoOrNull(selectedIsoDates[idx]);
         if (iso) conflictIsoSet.add(iso);
      }
   }

   const normalizedSelected = selectedIsoDates
      .map((iso) => normalizeIsoOrNull(iso))
      .filter(Boolean);
   return normalizedSelected.filter((iso) => conflictIsoSet.has(iso));
}

function conflictReasonForIso(errorLike, iso) {
   const reservationErrors = extractReservationErrors(errorLike);
   const target = normalizeIsoOrNull(iso);
   for (const item of reservationErrors) {
      const itemIso = normalizeIsoOrNull(item?.startTime);
      if (itemIso && target && itemIso === target) {
         return asStr(item?.message) || "Slot respins de server.";
      }
   }
   return "";
}

function sortIsosChronologically(isos = []) {
   return [...(isos || [])].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
   );
}

/**
 * Keep city lessons that stay after all poligon lessons and on different days.
 * Only the conflicting city slots are dropped — the rest remain selected.
 */
function pruneCityAgainstPoligon(poligonIsos = [], cityIsos = []) {
   const poligon = sortIsosChronologically(poligonIsos);
   const city = sortIsosChronologically(cityIsos);
   if (city.length === 0) return { kept: [], dropped: [] };
   if (poligon.length === 0) {
      return { kept: city, dropped: [] };
   }

   const poligonTs = poligon
      .map((iso) => new Date(iso).getTime())
      .filter((t) => Number.isFinite(t));
   const maxPoligonMs = poligonTs.length ? Math.max(...poligonTs) : null;
   const poligonDays = new Set(
      poligon.map((iso) => wallClockDayFromIso(iso)).filter(Boolean),
   );

   const kept = [];
   const dropped = [];
   for (const iso of city) {
      const ts = new Date(iso).getTime();
      const day = wallClockDayFromIso(iso);
      let reason = null;
      if (day && poligonDays.has(day)) {
         reason =
            "Se suprapune pe aceeași zi cu o lecție de pe poligon.";
      } else if (
         maxPoligonMs != null &&
         Number.isFinite(ts) &&
         ts <= maxPoligonMs
      ) {
         reason =
            "Intervalul rămâne înaintea (sau egal cu) lecțiile de pe poligon.";
      }
      if (reason) dropped.push({ iso, reason });
      else kept.push(iso);
   }

   return { kept, dropped };
}

function tipToInstructorMode(tip) {
   return tip === "single" ? "SINGLE" : "MULTIPLE";
}

function slotKeyForSelectedIso(iso) {
   return wallClockSlotKeyFromIso(iso);
}

export default function SAddProgAutoAssign({
   onClose,
   registerCloseInterceptor,
}) {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext);
   // Popup-ul programează întotdeauna pe CONTUL LOGAT (nu are selector de
   // elev). De pe admin/manager, lecțiile s-ar crea pe contul propriu, cu
   // instructorul ales de server (auto-assign) — „rezervări în alt loc".
   const normalizedAccountRole = String(user?.role || "")
      .trim()
      .toUpperCase();
   const isAutoAssignBlockedForRole =
      normalizedAccountRole === "ADMIN" || normalizedAccountRole === "MANAGER";
   const autoAssignBlockedMessage =
      "Programarea automată funcționează doar pe contul elevului. " +
      "De pe contul de admin/manager rezervările s-ar crea pe contul tău, " +
      "cu instructor ales automat. Folosește calendarul EXTREME pentru " +
      "programări manuale.";
   // Ca la examenul practic: elevul poate face rezervari doar in intervalul de
   // program, dupa ora Chisinaului. Examenul are fereastra lui, mai scurta.
   const { bookingWindowOpen, refreshBookingWindow } = useLessonBookingWindow();
   const fallbackMedicalDocsFlag = useMemo(
      () => readMedicalDocumentsFlag(user),
      [user],
   );

   const [medicalDocumentsOk, setMedicalDocumentsOk] = useState(
      () => fallbackMedicalDocsFlag,
   );
   const [medicalDocumentsChecked, setMedicalDocumentsChecked] =
      useState(false);
   const medicalDocumentsBlockedMessage =
      "Pentru a programa este necesar să prezinți documentele medicale. Dacă le-ai prezentat deja, contactează un manager pentru a primi acces la rezervări.";

   const [messages, setMessages] = useState([]);
   const notify = useCallback((type, text) => {
      setMessages((prev) => [
         ...prev,
         { id: `${Date.now()}-${Math.random()}`, type, text },
      ]);
   }, []);
   const dismissLast = useCallback(
      () => setMessages((prev) => prev.slice(0, -1)),
      [],
   );

   const safeClose = useCallback(
      (options = {}) => {
         if (typeof onClose === "function") onClose(options);
         else closePopupStore();
      },
      [onClose],
   );

   const [stage, setStage] = useState("setup"); // setup | pick
   const [numarLectii, setNumarLectii] = useState(15);
   const [cutie, setCutie] = useState("manual");
   const [sector, setSector] = useState("Botanica");
   const [tip, setTip] = useState("multiple"); // multiple | single

   const [userReservations, setUserReservations] = useState([]);
   const [lectiiExistente, setLectiiExistente] = useState(0);
   const [initLoading, setInitLoading] = useState(true);
   const [loading, setLoading] = useState(false);

   const [availabilityMap, setAvailabilityMap] = useState(() => new Map());
   const [requiresPoligon, setRequiresPoligon] = useState(false);

   const [data, setData] = useState(null);
   const [oraSelectata, setOraSelectata] = useState(null);
   /** Split selection: poligon (Botanica) then city sector. */
   const [poligonSelected, setPoligonSelected] = useState([]);
   const [citySelected, setCitySelected] = useState([]);
   const [pickTab, setPickTab] = useState("poligon"); // poligon | city
   const [showList, setShowList] = useState(false);
   const [rejected, setRejected] = useState([]);
   const [confirmDeleteKey, setConfirmDeleteKey] = useState(null);
   const [showExitPrompt, setShowExitPrompt] = useState(false);
   const [exitPromptAction, setExitPromptAction] = useState("setup");

   const submitGuardRef = useRef(false);
   const refreshInFlightRef = useRef(false);

   const minSelectableDate = useMemo(() => addDays(new Date(), 1), []);
   const maxSelectableDate = useMemo(() => addMonths(new Date(), 3), []);
   const minDay = useMemo(
      () => calendarDayStr(minSelectableDate),
      [minSelectableDate],
   );
   const maxDay = useMemo(
      () => calendarDayStr(maxSelectableDate),
      [maxSelectableDate],
   );

   const hardLock30 = lectiiExistente >= MAX_TOTAL_LESSONS;
   const hasValidReservationCheckpoint = useMemo(
      () => isAllowedReservationCheckpoint(lectiiExistente),
      [lectiiExistente],
   );
   const hasInvalidReservationCheckpoint = useMemo(
      () => !hardLock30 && !hasValidReservationCheckpoint,
      [hardLock30, hasValidReservationCheckpoint],
   );
   const invalidReservationCheckpointMessage = useMemo(
      () => getReservationCheckpointBlockMessage(lectiiExistente),
      [lectiiExistente],
   );
   const remainingTo30 = useMemo(
      () => Math.max(0, MAX_TOTAL_LESSONS - lectiiExistente),
      [lectiiExistente],
   );
   const requiredSubmitCount = useMemo(
      () => Math.min(Number(numarLectii) || 0, remainingTo30),
      [numarLectii, remainingTo30],
   );

   // Câte lecții pe poligon are elevul: fixat pe cont la prima programare
   // (adminul schimbă numărul doar pentru elevii noi). Implicit 7.
   const [polygonRequired, setPolygonRequired] = useState(
      MANDATORY_POLIGON_LESSONS,
   );
   useEffect(() => {
      let alive = true;
      getBookingSettings()
         .then((settings) => {
            const n = Number(settings?.myPolygonLessons ?? settings?.polygonLessons);
            if (alive && Number.isInteger(n) && n >= 0) setPolygonRequired(n);
         })
         .catch(() => {});
      return () => {
         alive = false;
      };
   }, []);

   // Sectoarele și poligoanele vin de la server (adminul le schimbă).
   const districts = useDistricts();
   const sectorOptions = useMemo(() => activeDistrictNames(districts), [districts]);
   const polygonOptions = useMemo(() => activePolygonNames(districts), [districts]);
   useEffect(() => {
      if (sectorOptions.length && !sectorOptions.includes(sector)) {
         setSector(sectorOptions[0]);
      }
   }, [sectorOptions, sector]);

   // Poligonul: cel salvat pe cont (se alege o dată), singurul poligon sau
   // cel ales acum în formular.
   const [savedPolygon, setSavedPolygon] = useState(null);
   const [polygonChoice, setPolygonChoice] = useState("");
   const [serverAskedPolygon, setServerAskedPolygon] = useState(false);
   const [serverPolygon, setServerPolygon] = useState(null);
   useEffect(() => {
      setServerAskedPolygon(false);
   }, [sector]);
   const savedPolygonName = useMemo(
      () =>
         savedPolygon
            ? polygonOptions.find((n) => districtKey(n) === districtKey(savedPolygon)) || null
            : null,
      [polygonOptions, savedPolygon],
   );
   const polygonName =
      savedPolygonName ||
      (polygonOptions.length === 1
         ? polygonOptions[0]
         : polygonOptions.includes(polygonChoice)
           ? polygonChoice
           : null);

   const remainingPoligon = useMemo(
      () =>
         remainingPoligonLessonsNeeded(
            sector,
            userReservations,
            polygonRequired,
            districts,
         ),
      [sector, userReservations, polygonRequired, districts],
   );

   const mustChoosePolygon =
      polygonOptions.length > 1 &&
      !savedPolygonName &&
      (remainingPoligon > 0 || serverAskedPolygon);
   const polygonLabel = serverPolygon || polygonName || "poligon";

   const needsPoligonTabs =
      requiresPoligon || sectorRequiresPoligon(sector, districts);

   const selectedDates = useMemo(
      () => [
         ...sortIsosChronologically(poligonSelected),
         ...sortIsosChronologically(citySelected),
      ],
      [poligonSelected, citySelected],
   );

   // cel mult cât pachetul ales: dacă pe poligon sunt mai multe lecții decât
   // în pachet, pachetul se face tot pe poligon, iar restul vine în următorul
   const poligonNeeded = needsPoligonTabs
      ? Math.min(remainingPoligon, requiredSubmitCount)
      : 0;
   const cityNeeded = Math.max(0, requiredSubmitCount - poligonNeeded);
   const poligonComplete = poligonSelected.length >= poligonNeeded;
   const cityUnlocked = !needsPoligonTabs || poligonComplete;

   const activeForcePool = useMemo(() => {
      if (!needsPoligonTabs) return "sector";
      return pickTab === "poligon" ? "poligon" : "sector";
   }, [needsPoligonTabs, pickTab]);

   const tabReachedLimit = useMemo(() => {
      if (!needsPoligonTabs) {
         return selectedDates.length >= requiredSubmitCount;
      }
      if (pickTab === "poligon") {
         return poligonSelected.length >= poligonNeeded;
      }
      return citySelected.length >= cityNeeded;
   }, [
      needsPoligonTabs,
      pickTab,
      poligonSelected.length,
      citySelected.length,
      poligonNeeded,
      cityNeeded,
      selectedDates.length,
      requiredSubmitCount,
   ]);

   const existingBookedDaySet = useMemo(() => {
      const set = new Set();
      for (const r of userReservations || []) {
         const st = getStartFromReservation(r);
         if (!st) continue;
         const day =
            wallClockDayFromIso(st) ||
            busyLocalKeyFromStored(st)?.split("|")[0] ||
            localDateStrTZ(new Date(st), MOLDOVA_TZ);
         if (day) set.add(day);
      }
      return set;
   }, [userReservations]);

   const selectedBookedDaySet = useMemo(() => {
      const set = new Set();
      for (const iso of selectedDates || []) {
         const day = wallClockDayFromIso(iso);
         if (day) set.add(day);
      }
      return set;
   }, [selectedDates]);

   const bookedDaySet = useMemo(() => {
      const set = new Set(existingBookedDaySet);
      for (const d of selectedBookedDaySet) set.add(d);
      return set;
   }, [existingBookedDaySet, selectedBookedDaySet]);

   const selectedKeySet = useMemo(() => {
      const set = new Set();
      for (const iso of selectedDates || []) {
         const key = wallClockSlotKeyFromIso(iso);
         if (key) set.add(key);
      }
      return set;
   }, [selectedDates]);

   const totalPickedCount = selectedDates.length + rejected.length;
   const reachedPackLimit = totalPickedCount >= requiredSubmitCount;
   const canSubmitInPick =
      requiredSubmitCount > 0 &&
      selectedDates.length === requiredSubmitCount &&
      rejected.length === 0 &&
      (!needsPoligonTabs ||
         (poligonSelected.length === poligonNeeded &&
            citySelected.length === cityNeeded));

   // Auto-open list when package selection is complete.
   useEffect(() => {
      if (stage !== "pick") return;
      if (canSubmitInPick) setShowList(true);
   }, [canSubmitInPick, stage]);

   const dayLocal = data ? calendarDayStr(data) : null;
   const dayIsBooked = dayLocal ? bookedDaySet.has(dayLocal) : false;

   const showLoadingScreen = initLoading || !medicalDocumentsChecked;

   useEffect(() => {
      let alive = true;
      (async () => {
         setMedicalDocumentsChecked(false);
         try {
            const me = await fetchUserInfo();
            if (!alive) return;
            setMedicalDocumentsOk(readMedicalDocumentsFlag(me));
            setSavedPolygon(me?.polygonDistrict || null);
         } catch {
            if (!alive) return;
            setMedicalDocumentsOk(fallbackMedicalDocsFlag);
         } finally {
            if (alive) setMedicalDocumentsChecked(true);
         }
      })();
      return () => {
         alive = false;
      };
   }, [user?.id, fallbackMedicalDocsFlag]);

   useEffect(() => {
      let alive = true;
      (async () => {
         setInitLoading(true);
         try {
            if (!user?.id) {
               setUserReservations([]);
               setLectiiExistente(0);
               return;
            }
            const existing = await dispatch(fetchUserReservations(user.id))
               .unwrap()
               .catch(() => []);
            if (!alive) return;
            const arr = Array.isArray(existing) ? existing : [];
            setUserReservations(arr);
            setLectiiExistente(arr.length);
         } finally {
            if (alive) setInitLoading(false);
         }
      })();
      return () => {
         alive = false;
      };
   }, [dispatch, user?.id]);

   const resetPickState = useCallback(() => {
      setData(null);
      setOraSelectata(null);
      setPoligonSelected([]);
      setCitySelected([]);
      setPickTab("poligon");
      setRejected([]);
      setShowList(false);
      setConfirmDeleteKey(null);
   }, []);

   const loadAvailability = useCallback(async () => {
      const gearbox = normalizeGearboxLabel(cutie);
      const startDay = calendarDayStr(minSelectableDate);
      const endDay = calendarDayStr(maxSelectableDate);
      const payload = await ReservationsAPI.getReservationAvailability({
         sector,
         gearbox,
         startDate: `${startDay}T00:00:00.000Z`,
         endDate: `${endDay}T23:59:59.999Z`,
         instructorMode: tipToInstructorMode(tip),
         polygonSector: polygonName || undefined,
      });
      const map = buildAvailabilitySlotMap(payload);
      setRequiresPoligon(
         Boolean(payload?.requiresPoligon) ||
            sectorRequiresPoligon(sector, districts),
      );
      setServerPolygon(payload?.poligonSector || null);
      setAvailabilityMap(map);
      return { payload, map };
   }, [
      cutie,
      sector,
      tip,
      minSelectableDate,
      maxSelectableDate,
      polygonName,
      districts,
   ]);

   const refreshAvailability = useCallback(
      async ({ silent = true } = {}) => {
         if (refreshInFlightRef.current) return;
         if (stage !== "pick") return;
         refreshInFlightRef.current = true;
         try {
            await loadAvailability();
         } catch (e) {
            if (!silent) {
               notify(
                  "error",
                  "Nu am putut reîmprospăta disponibilitatea sloturilor.",
               );
               if (e?.message) notify("alert", `Detalii: ${e.message}`);
            }
         } finally {
            refreshInFlightRef.current = false;
         }
      },
      [loadAvailability, notify, stage],
   );

   const handleContinue = async () => {
      if (isAutoAssignBlockedForRole) {
         notify("warn", autoAssignBlockedMessage);
         return;
      }
      if (!medicalDocumentsOk) {
         notify("warn", medicalDocumentsBlockedMessage);
         return;
      }
      if (hardLock30 || remainingTo30 <= 0) {
         notify(
            "warn",
            `Ai deja ${MAX_TOTAL_LESSONS} lecții programate. Nu mai poți adăuga.`,
         );
         return;
      }
      if (hasInvalidReservationCheckpoint) {
         notify("warn", invalidReservationCheckpointMessage);
         return;
      }
      if (Number(numarLectii) !== 15 && Number(numarLectii) !== 30) {
         notify("warn", "Selectează pachetul (15 sau 30) ca să continui.");
         return;
      }
      if (mustChoosePolygon && !polygonName) {
         notify("warn", POLYGON_CHOICE_MESSAGE);
         return;
      }
      setLoading(true);
      try {
         const gearbox = normalizeGearboxLabel(cutie);
         const eligible = await ReservationsAPI.getEligibleInstructors({
            sector,
            gearbox,
            polygonSector: polygonName || undefined,
         });

         // serverul cere alegerea (ex. adminul tocmai a adăugat un poligon)
         if (eligible?.polygonChoiceRequired) {
            setServerAskedPolygon(true);
            reloadDistricts().catch(() => {});
            notify("warn", POLYGON_CHOICE_MESSAGE);
            return;
         }

         if (!hasEligibleInstructors(eligible)) {
            notify(
               "warn",
               "Nu există instructori eligibili pentru sectorul și cutia selectate.",
            );
            return;
         }

         resetPickState();
         const { payload } = await loadAvailability();
         const needs =
            Boolean(payload?.requiresPoligon) ||
            sectorRequiresPoligon(sector, districts);
         const poligonLeft = remainingPoligonLessonsNeeded(
            sector,
            userReservations,
            polygonRequired,
            districts,
         );
         setPickTab(needs && poligonLeft > 0 ? "poligon" : "city");
         setStage("pick");
         setShowList(false);
      } catch (e) {
         notify(
            "error",
            "Nu am putut pregăti disponibilitatea pentru programare.",
         );
         if (e?.message) notify("alert", `Detalii: ${e.message}`);
      } finally {
         setLoading(false);
      }
   };

   const isSlotDisabledForKey = useCallback(
      (key, candidateIso) => {
         if (!key || !candidateIso) return true;
         if (selectedKeySet.has(key)) return true;
         if (needsPoligonTabs && pickTab === "city" && !cityUnlocked) {
            return true;
         }
         if (
            needsPoligonTabs &&
            pickTab === "city" &&
            poligonSelected.length > 0
         ) {
            const maxPoligonMs = Math.max(
               ...poligonSelected.map((iso) => new Date(iso).getTime()),
            );
            if (
               Number.isFinite(maxPoligonMs) &&
               new Date(candidateIso).getTime() <= maxPoligonMs
            ) {
               return true;
            }
         }
         const meta = availabilityMap.get(key);
         // Absent from API map OR relevant free count === 0 → blocked.
         return !isAvailabilitySlotFree({
            slotMeta: meta,
            candidateIso,
            selectedIsos: selectedDates,
            remainingPoligon,
            requiresPoligon,
            forcePool: activeForcePool,
         });
      },
      [
         activeForcePool,
         availabilityMap,
         cityUnlocked,
         needsPoligonTabs,
         pickTab,
         poligonSelected,
         remainingPoligon,
         requiresPoligon,
         selectedDates,
         selectedKeySet,
      ],
   );

   const getSlotAvailabilityTitle = useCallback(
      (key, candidateIso, { alreadyPicked, dayBooked } = {}) => {
         if (!data) return "Selectează o zi mai întâi";
         if (dayBooked) return "Ai deja o programare în această zi";
         if (alreadyPicked) return "Deja adăugat";
         if (needsPoligonTabs && pickTab === "city" && !cityUnlocked) {
            return `Finalizează mai întâi cele ${poligonNeeded} lecții de pe poligon (${poligonSelected.length}/${poligonNeeded}).`;
         }
         if (tabReachedLimit) {
            if (needsPoligonTabs && pickTab === "poligon") {
               return `Poligon complet (${poligonNeeded}/${poligonNeeded}). Continuă pe tab-ul Oraș.`;
            }
            return `Ai atins limita (${requiredSubmitCount}).`;
         }

         const meta = key ? availabilityMap.get(key) : null;
         const count = getRelevantFreeCount(meta, {
            requiresPoligon,
            remainingPoligon,
            candidateIso,
            selectedIsos: selectedDates,
            forcePool: activeForcePool,
         });

         if (count < 0) return "Indisponibil (ora nu e oferită)";
         if (count === 0) {
            return activeForcePool === "poligon"
               ? "Indisponibil pe poligon (0 instructori liberi)"
               : "Indisponibil pe sector (0 instructori liberi)";
         }
         return activeForcePool === "poligon"
            ? tip === "single"
               ? "Disponibil pe poligon (1 instructor)"
               : `Disponibil pe poligon (${count} instructori)`
            : tip === "single"
              ? "Disponibil pe sector (1 instructor)"
              : `Disponibil (${count} instructori)`;
      },
      [
         activeForcePool,
         availabilityMap,
         cityUnlocked,
         data,
         needsPoligonTabs,
         pickTab,
         poligonNeeded,
         poligonSelected.length,
         remainingPoligon,
         requiredSubmitCount,
         requiresPoligon,
         selectedDates,
         tabReachedLimit,
         tip,
      ],
   );

   const isDaySelectable = useCallback(
      (date) => {
         if (isPastOrTodayInMoldova(date)) return false;
         const day = calendarDayStr(date);
         if (bookedDaySet.has(day)) return false;
         if (day < minDay || day > maxDay) return false;
         if (needsPoligonTabs && pickTab === "city" && !cityUnlocked) {
            return false;
         }
         if (tabReachedLimit) return false;

         // City lessons must be after all poligon lessons (no "dates behind").
         if (
            needsPoligonTabs &&
            pickTab === "city" &&
            poligonSelected.length > 0
         ) {
            const maxPoligonMs = Math.max(
               ...poligonSelected.map((iso) => new Date(iso).getTime()),
            );
            if (Number.isFinite(maxPoligonMs)) {
               // Compare end-of-day loosely via candidate slots below; skip day
               // entirely if its last slot cannot beat max poligon time.
               const lastIso = buildIsoForLocalDayTime(day, "18:00");
               if (
                  lastIso &&
                  new Date(lastIso).getTime() <= maxPoligonMs
               ) {
                  return false;
               }
            }
         }

         return dayHasAnyFreeSlot({
            dayLocal: day,
            slotLabels: SLOT_LABELS,
            availabilityMap,
            selectedIsos: selectedDates,
            remainingPoligon,
            requiresPoligon,
            forcePool: activeForcePool,
            buildCandidateIso: buildIsoForLocalDayTime,
         });
      },
      [
         activeForcePool,
         availabilityMap,
         bookedDaySet,
         cityUnlocked,
         maxDay,
         minDay,
         needsPoligonTabs,
         pickTab,
         poligonSelected,
         remainingPoligon,
         requiresPoligon,
         selectedDates,
         tabReachedLimit,
      ],
   );

   const adaugaProgramare = () => {
      if (!data || !oraSelectata) return;

      if (!isLessonBookingWindowOpen()) {
         refreshBookingWindow();
         notify("warn", LESSON_BOOKING_WINDOW_BLOCKED_MESSAGE);
         return;
      }
      if (hardLock30 || remainingTo30 <= 0) {
         notify(
            "warn",
            `Ai deja ${MAX_TOTAL_LESSONS} lecții programate. Nu mai poți adăuga.`,
         );
         return;
      }
      if (hasInvalidReservationCheckpoint) {
         notify("warn", invalidReservationCheckpointMessage);
         return;
      }
      if (requiredSubmitCount <= 0) {
         notify("warn", "Nu mai ai loc pentru lecții noi.");
         return;
      }
      if (isPastOrTodayInMoldova(data)) {
         notify("warn", "Nu poți programa pentru azi sau în trecut.");
         return;
      }

      const dayLocalStr = calendarDayStr(data);
      if (bookedDaySet.has(dayLocalStr)) {
         notify("warn", "Ai deja o programare în această zi. Alege altă zi.");
         return;
      }
      if (reachedPackLimit) {
         notify("warn", `Ai atins limita curentă (${requiredSubmitCount}).`);
         return;
      }

      const addingPoligon =
         needsPoligonTabs && pickTab === "poligon";
      const addingCity = needsPoligonTabs && pickTab === "city";

      if (addingCity && !cityUnlocked) {
         notify(
            "warn",
            `Selectează mai întâi toate lecțiile de pe poligon (${poligonSelected.length}/${poligonNeeded}).`,
         );
         return;
      }
      if (addingPoligon && poligonSelected.length >= poligonNeeded) {
         notify(
            "warn",
            `Poligonul e complet (${poligonNeeded}/${poligonNeeded}). Continuă pe tab-ul Oraș (${sector}).`,
         );
         setPickTab("city");
         return;
      }
      if (addingCity && citySelected.length >= cityNeeded) {
         notify("warn", `Ai atins limita pentru ${sector} (${cityNeeded}).`);
         return;
      }

      const iso = buildLocalMatchIso(dayLocalStr, oraSelectata.oraStart);
      if (!iso) {
         notify("warn", "Nu am putut construi ora selectată.");
         return;
      }
      const key = availabilitySlotKey(dayLocalStr, oraSelectata.oraStart);

      if (selectedKeySet.has(key)) {
         notify("warn", "Intervalul este deja în listă.");
         return;
      }

      if (addingCity && poligonSelected.length > 0) {
         const maxPoligonMs = Math.max(
            ...poligonSelected.map((x) => new Date(x).getTime()),
         );
         if (
            Number.isFinite(maxPoligonMs) &&
            new Date(iso).getTime() <= maxPoligonMs
         ) {
            notify(
               "warn",
               "Pe Oraș nu poți alege o dată/oră înaintea lecțiilor de pe poligon. Alege un interval ulterior.",
            );
            return;
         }
      }

      if (
         isSlotDisabledForKey(key, iso) &&
         !selectedKeySet.has(key)
      ) {
         notify("warn", "Intervalul nu mai este disponibil.");
         return;
      }

      const nextPoligon = addingPoligon
         ? sortIsosChronologically([...poligonSelected, iso])
         : sortIsosChronologically(poligonSelected);
      const nextCity = addingCity
         ? sortIsosChronologically([...citySelected, iso])
         : needsPoligonTabs
           ? sortIsosChronologically(citySelected)
           : sortIsosChronologically([...citySelected, iso]);
      const next = [...nextPoligon, ...nextCity];

      const { ok, conflicts } = partitionSlotsByAvailability({
         selectedIsos: next,
         availabilityMap,
         remainingPoligon: poligonNeeded,
         requiresPoligon: needsPoligonTabs,
         slotKeyForIso: slotKeyForSelectedIso,
         stabilize: true,
      });

      if (!ok.includes(iso)) {
         notify(
            "warn",
            "Intervalul nu e compatibil cu selecția curentă (poligon/sector).",
         );
         return;
      }

      // Keep bucket membership for surviving slots.
      const okSet = new Set(ok);
      let keptPoligon = nextPoligon.filter((x) => okSet.has(x));
      let keptCity = nextCity.filter((x) => okSet.has(x));

      // After a poligon add/change, drop only city lessons that conflict.
      if (needsPoligonTabs && (addingPoligon || keptPoligon.length > 0)) {
         const pruned = pruneCityAgainstPoligon(keptPoligon, keptCity);
         keptCity = pruned.kept;
         if (pruned.dropped.length) {
            for (const c of pruned.dropped) {
               if (!conflicts.some((x) => x.iso === c.iso)) {
                  conflicts.push(c);
               }
            }
         }
      }

      setPoligonSelected(keptPoligon);
      setCitySelected(keptCity);

      if (conflicts.length) {
         setRejected((prev) => mergeRejected(prev, conflicts));
         setShowList(true);
         const cityConflictCount = conflicts.filter((c) =>
            nextCity.includes(c.iso),
         ).length;
         if (addingPoligon && cityConflictCount > 0) {
            notify(
               "warn",
               `${cityConflictCount} lecție${cityConflictCount === 1 ? "" : "i"} din Oraș ${cityConflictCount === 1 ? "a fost anulată" : "au fost anulate"} din cauza suprapunerii. Alege încă ${cityConflictCount} pe tab-ul Oraș.`,
            );
         } else {
            notify(
               "warn",
               `${conflicts.length} ore din listă au devenit invalide după această adăugare. Verifică conflictele.`,
            );
         }
      }

      setOraSelectata(null);
      setData(null);

      const nextPoligonCount = keptPoligon.length;
      if (
         needsPoligonTabs &&
         addingPoligon &&
         nextPoligonCount >= poligonNeeded &&
         cityNeeded > 0
      ) {
         setPickTab("city");
         const cityLeft = Math.max(0, cityNeeded - keptCity.length);
         if (cityLeft > 0) {
            notify(
               "info",
               `Poligon complet. Mai ai de ales ${cityLeft} lecție${cityLeft === 1 ? "" : "i"} pe Oraș (${sector}).`,
            );
         } else {
            notify(
               "info",
               `Poligon complet (${poligonNeeded}/${poligonNeeded}). Continuă pe Oraș (${sector}).`,
            );
         }
      } else if (!conflicts.length) {
         notify("info", "Lecție adăugată în listă.");
      }
   };

   const stergeSelected = (iso) => {
      const wasPoligon = poligonSelected.includes(iso);
      if (wasPoligon) {
         const nextPoligon = poligonSelected.filter((x) => x !== iso);
         const { kept, dropped } = pruneCityAgainstPoligon(
            nextPoligon,
            citySelected,
         );
         setPoligonSelected(nextPoligon);
         setCitySelected(kept);

         if (dropped.length) {
            setRejected((prev) => mergeRejected(prev, dropped));
            setShowList(true);
            notify(
               "warn",
               `Am anulat ${dropped.length} lecție${dropped.length === 1 ? "" : "i"} din Oraș care se suprapuneau. Completează poligonul, apoi alege doar locurile lipsă pe Oraș.`,
            );
         } else if (needsPoligonTabs && nextPoligon.length < poligonNeeded) {
            notify(
               "info",
               `Lecție de poligon ștearsă. Completează din nou poligonul (${nextPoligon.length}/${poligonNeeded}) — lecțiile din Oraș rămân.`,
            );
         }

         if (needsPoligonTabs && nextPoligon.length < poligonNeeded) {
            setPickTab("poligon");
         }
      } else {
         setCitySelected((prev) => prev.filter((x) => x !== iso));
      }
      setConfirmDeleteKey(null);
   };

   const stergeRejected = (iso) => {
      setRejected((prev) => prev.filter((x) => x.iso !== iso));
      setConfirmDeleteKey(null);
   };

   const mergeRejected = (prev, next) => {
      const map = new Map();
      for (const x of prev || []) map.set(x.iso, x.reason || "Conflict");
      for (const x of next || []) map.set(x.iso, x.reason || "Conflict");
      return Array.from(map.entries()).map(([iso, reason]) => ({
         iso,
         reason,
      }));
   };

   const goBackToSetup = useCallback(() => {
      resetPickState();
      setAvailabilityMap(new Map());
      setStage("setup");
      setShowExitPrompt(false);
   }, [resetPickState]);

   const hasSelectedLessons =
      selectedDates.length > 0 || rejected.length > 0;

   const requestExit = useCallback(
      (action) => {
         // Avertizarea apare doar dacă există minim o lecție selectată.
         if (!hasSelectedLessons) {
            if (action === "close") safeClose({ skipConfirm: true });
            else goBackToSetup();
            return;
         }
         setExitPromptAction(action);
         setShowExitPrompt(true);
      },
      [goBackToSetup, hasSelectedLessons, safeClose],
   );

   const confirmExitPrompt = useCallback(() => {
      setShowExitPrompt(false);
      if (exitPromptAction === "close") {
         resetPickState();
         safeClose({ skipConfirm: true });
         return;
      }
      goBackToSetup();
   }, [exitPromptAction, goBackToSetup, resetPickState, safeClose]);

   useEffect(() => {
      if (typeof registerCloseInterceptor !== "function") return undefined;
      return registerCloseInterceptor(() => {
         // Fără lecții selectate → lasă popup-ul să se închidă normal.
         if (!hasSelectedLessons) return true;
         setExitPromptAction("close");
         setShowExitPrompt(true);
         return false;
      });
   }, [hasSelectedLessons, registerCloseInterceptor]);

   const handleTopbarPrimaryAction = () => {
      if (showList) {
         setShowList(false);
         return;
      }
      requestExit("setup");
   };

   const handleTopbarSecondaryAction = () => {
      setShowList((v) => !v);
   };

   const trimiteProgramari = async () => {
      if (submitGuardRef.current) return;
      if (isAutoAssignBlockedForRole) {
         notify("warn", autoAssignBlockedMessage);
         return;
      }
      if (!isLessonBookingWindowOpen()) {
         refreshBookingWindow();
         notify("warn", LESSON_BOOKING_WINDOW_BLOCKED_MESSAGE);
         return;
      }
      if (!canSubmitInPick) {
         notify(
            "warn",
            `Trebuie să selectezi exact ${requiredSubmitCount} lecții înainte de trimitere.`,
         );
         return;
      }

      submitGuardRef.current = true;
      setLoading(true);

      try {
         const latest = await dispatch(fetchUserReservations(user?.id))
            .unwrap()
            .catch(() => userReservations);
         const latestArr = Array.isArray(latest) ? latest : [];
         setUserReservations(latestArr);
         setLectiiExistente(latestArr.length);

         const remain = Math.max(0, MAX_TOTAL_LESSONS - latestArr.length);
         const mustSubmitExactly = Math.min(Number(numarLectii) || 0, remain);

         if (mustSubmitExactly <= 0) {
            notify("warn", "Nu mai poți adăuga lecții.");
            return;
         }
         if (selectedDates.length !== mustSubmitExactly) {
            notify(
               "warn",
               `Trebuie să selectezi exact ${mustSubmitExactly} lecții înainte de trimitere.`,
            );
            return;
         }

         const { map: freshMap } = await loadAvailability();
         const poligonLeft = remainingPoligonLessonsNeeded(
            sector,
            latestArr,
            polygonRequired,
            districts,
         );
         const needsPoligon =
            Boolean(requiresPoligon) || sectorRequiresPoligon(sector, districts);

         const { ok, conflicts } = partitionSlotsByAvailability({
            selectedIsos: selectedDates,
            availabilityMap: freshMap,
            remainingPoligon: poligonLeft,
            requiresPoligon: needsPoligon,
            slotKeyForIso: slotKeyForSelectedIso,
            stabilize: true,
         });

         if (conflicts.length) {
            setRejected((prev) => mergeRejected(prev, conflicts));
            const okSet = new Set(ok);
            setPoligonSelected((prev) => prev.filter((iso) => okSet.has(iso)));
            setCitySelected((prev) => prev.filter((iso) => okSet.has(iso)));
            setShowList(true);
            await refreshAvailability({ silent: true });
            notify(
               "warn",
               `${conflicts.length} ore din selecție nu sunt valide împreună (poligon/sector sau 0 instructori). Înlocuiește-le pe toate dintr-o dată.`,
            );
            return;
         }

         if (ok.length !== mustSubmitExactly) {
            const okSet = new Set(ok);
            setPoligonSelected((prev) => prev.filter((iso) => okSet.has(iso)));
            setCitySelected((prev) => prev.filter((iso) => okSet.has(iso)));
            setShowList(true);
            notify(
               "warn",
               `După verificare au rămas ${ok.length}/${mustSubmitExactly}. Ajustează selecția.`,
            );
            return;
         }

         const gearbox = normalizeGearboxLabel(cutie);
         // Selection is already wall-clock (ex. 07:00 / 15:00 as in /availability).
         // Convert to API startTimes only here — no ±TZ on the UI path.
         const startTimes = startTimesForAutoAssign(ok);

         const basePayload = {
            sector,
            gearbox,
            instructorMode: tipToInstructorMode(tip),
            startTimes,
            privateMessage: "",
            color: "--black-t",
            isImportant: true,
            // poligonul ales; serverul îl salvează pe cont la prima programare
            ...(polygonName ? { polygonSector: polygonName } : {}),
         };

         notify("info", "Trimit programările...");

         const createResult =
            await ReservationsAPI.autoAssignReservations(basePayload);

         let refreshedReservations = null;
         try {
            if (user?.id) {
               const arr = await dispatch(
                  fetchUserReservations(user.id),
               ).unwrap();
               refreshedReservations = Array.isArray(arr) ? arr : [];
               setUserReservations(refreshedReservations);
               setLectiiExistente(refreshedReservations.length);
            }
         } catch {
            /* ignore refresh errors after success */
         }

         // Modul „Unul": instructorul cu cele mai multe lecții devine
         // instructorul preferat din profil. Eșecul nu blochează rezervările.
         if (tip === "single") {
            try {
               const createdList = Array.isArray(createResult)
                  ? createResult
                  : Array.isArray(createResult?.reservations)
                    ? createResult.reservations
                    : [];
               const source = refreshedReservations?.length
                  ? refreshedReservations
                  : createdList;
               const dominantId =
                  dominantInstructorIdFromReservations(source);
               if (dominantId) await setDesiredInstructor(dominantId);
            } catch {
               /* profilul rămâne cum era dacă endpoint-ul pică */
            }
         }

         resetPickState();
         setStage("setup");
         safeClose({ skipConfirm: true });
         setTimeout(() => {
            window.location.reload();
         }, 0);
      } catch (e) {
         const serverConflicts = extractServerConflictSelectedDates(
            e,
            selectedDates,
         );

         if (serverConflicts.length) {
            const normalizedDropSet = new Set(
               serverConflicts
                  .map((iso) => normalizeIsoOrNull(iso))
                  .filter(Boolean),
            );

            setRejected((prev) =>
               mergeRejected(
                  prev,
                  serverConflicts.map((iso) => ({
                     iso,
                     reason: conflictReasonForIso(e, iso),
                  })),
               ),
            );
            setPoligonSelected((prev) =>
               prev.filter((iso) => {
                  const norm = normalizeIsoOrNull(iso);
                  return !norm || !normalizedDropSet.has(norm);
               }),
            );
            setCitySelected((prev) =>
               prev.filter((iso) => {
                  const norm = normalizeIsoOrNull(iso);
                  return !norm || !normalizedDropSet.has(norm);
               }),
            );
            setShowList(true);
            await refreshAvailability({ silent: true });
            notify(
               "warn",
               `Serverul a respins ${serverConflicts.length} sloturi. Le-am mutat la conflicte.`,
            );
            return;
         }

         // serverul cere alegerea poligonului: înapoi la criterii, unde apare
         if (String(e?.message || "").includes(POLYGON_CHOICE_MESSAGE)) {
            setServerAskedPolygon(true);
            reloadDistricts().catch(() => {});
            resetPickState();
            setStage("setup");
            notify("warn", POLYGON_CHOICE_MESSAGE);
            return;
         }

         notify("error", "A apărut o eroare la trimitere.");
         if (e?.message) notify("alert", `Detalii: ${e.message}`);
      } finally {
         submitGuardRef.current = false;
         setLoading(false);
      }
   };

   const exitPromptTitle =
      exitPromptAction === "close"
         ? "Sigur închizi popup-ul?"
         : "Sigur revii la criterii?";
   const exitPromptText =
      exitPromptAction === "close"
         ? "Dacă închizi acum popup-ul, toate selecțiile și progresul din acest pas se pierd."
         : "Dacă ieși acum din selecție, toate orele alese și conflictele din acest pas se pierd.";
   const exitPromptCancelLabel =
      exitPromptAction === "close" ? "Rămâi în popup" : "Rămâi aici";
   const exitPromptConfirmLabel =
      exitPromptAction === "close"
         ? "Da, închide popup-ul"
         : "Da, revino la criterii";

   return (
      <>
         <AlertPills messages={messages} onDismiss={dismissLast} />
         <div className="studentAddReservation">
            <h3 className="studentAddReservation__title-main">
               Adaugă rezervări
            </h3>

            <div className="studentAddReservation__content">
               {isAutoAssignBlockedForRole ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Programarea automată este dezactivată pentru
                        admin/manager.</b>{" "}
                        {autoAssignBlockedMessage}
                     </div>
                  </div>
               ) : showLoadingScreen ? (
                  <div className="studentAddReservation__loading">
                     Se încarcă…
                  </div>
               ) : !medicalDocumentsOk ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Programările sunt blocate.</b>{" "}
                        {medicalDocumentsBlockedMessage}
                     </div>
                  </div>
               ) : hardLock30 ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Ai deja {MAX_TOTAL_LESSONS} lecții programate.</b> Nu
                        mai poți adăuga alte programări.
                     </div>
                  </div>
               ) : hasInvalidReservationCheckpoint ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>Programările sunt blocate.</b>{" "}
                        {invalidReservationCheckpointMessage}
                     </div>
                  </div>
               ) : !bookingWindowOpen ? (
                  <div className="studentAddReservation__box">
                     <div className="studentAddReservation__info">
                        <b>{LESSON_BOOKING_WINDOW_FULL_LABEL}</b>{" "}
                        {LESSON_BOOKING_WINDOW_BLOCKED_MESSAGE}
                     </div>
                  </div>
               ) : stage === "setup" ? (
                  <>
                     <div className="studentAddReservation__selector studentAddReservation__selector--row">
                        <h3 className="studentAddReservation__title">
                           Selectează pachetul de lecții
                        </h3>
                        <div
                           className={`studentAddReservation__radio-wrapper ${
                              numarLectii === 15
                                 ? "studentAddReservation__radio-wrapper--left"
                                 : "studentAddReservation__radio-wrapper--right"
                           }`}
                        >
                           <label htmlFor="auto-lectii-15" style={{ cursor: "pointer" }}>
                              <input
                                 id="auto-lectii-15"
                                 type="radio"
                                 name="auto-lectii"
                                 value={15}
                                 checked={Number(numarLectii) === 15}
                                 onChange={(e) =>
                                    setNumarLectii(Number(e.target.value))
                                 }
                              />
                              15 lecții
                           </label>
                           <label htmlFor="auto-lectii-30" style={{ cursor: "pointer" }}>
                              <input
                                 id="auto-lectii-30"
                                 type="radio"
                                 name="auto-lectii"
                                 value={30}
                                 checked={Number(numarLectii) === 30}
                                 onChange={(e) =>
                                    setNumarLectii(Number(e.target.value))
                                 }
                              />
                              30 lecții
                           </label>
                        </div>
                     </div>

                     <div className="studentAddReservation__selector studentAddReservation__selector--row">
                        <h3 className="studentAddReservation__title">
                           Selectează cutia de viteza
                        </h3>
                        <div
                           className={`studentAddReservation__radio-wrapper ${
                              cutie === "manual"
                                 ? "studentAddReservation__radio-wrapper--left"
                                 : "studentAddReservation__radio-wrapper--right"
                           }`}
                        >
                           <label>
                              <input
                                 type="radio"
                                 name="auto-cutie"
                                 value="manual"
                                 checked={cutie === "manual"}
                                 onChange={(e) => setCutie(e.target.value)}
                              />
                              Manual
                           </label>
                           <label>
                              <input
                                 type="radio"
                                 name="auto-cutie"
                                 value="automat"
                                 checked={cutie === "automat"}
                                 onChange={(e) => setCutie(e.target.value)}
                              />
                              Automat
                           </label>
                        </div>
                     </div>

                     <div className="studentAddReservation__selector studentAddReservation__selector--row">
                        <h3 className="studentAddReservation__title">
                           Selectează sectorul dorit
                        </h3>
                        <div className="studentAddReservation__radio-wrapper studentAddReservation__radio-wrapper--triple">
                           {sectorOptions.map((opt) => (
                              <label
                                 key={opt}
                                 className={sector === opt ? "is-active" : ""}
                              >
                                 <input
                                    type="radio"
                                    name="auto-sector"
                                    value={opt}
                                    checked={sector === opt}
                                    onChange={(e) => setSector(e.target.value)}
                                 />
                                 {opt}
                              </label>
                           ))}
                        </div>
                     </div>

                     {mustChoosePolygon ? (
                        <div className="studentAddReservation__selector studentAddReservation__selector--row">
                           <h3 className="studentAddReservation__title">
                              Alege poligonul (primele lecții; se alege o
                              singură dată)
                           </h3>
                           <div className="studentAddReservation__radio-wrapper studentAddReservation__radio-wrapper--triple">
                              {polygonOptions.map((opt) => (
                                 <label
                                    key={opt}
                                    className={
                                       polygonChoice === opt ? "is-active" : ""
                                    }
                                 >
                                    <input
                                       type="radio"
                                       name="auto-polygon"
                                       value={opt}
                                       checked={polygonChoice === opt}
                                       onChange={(e) =>
                                          setPolygonChoice(e.target.value)
                                       }
                                    />
                                    {opt}
                                 </label>
                              ))}
                           </div>
                        </div>
                     ) : null}

                     <div className="studentAddReservation__selector studentAddReservation__selector--row">
                        <h3 className="studentAddReservation__title">
                           Alege tipul de repartizare a rezervărilor
                        </h3>
                        <div
                           className={`studentAddReservation__radio-wrapper ${
                              tip === "multiple"
                                 ? "studentAddReservation__radio-wrapper--left"
                                 : "studentAddReservation__radio-wrapper--right"
                           }`}
                        >
                           <label>
                              <input
                                 type="radio"
                                 name="auto-tip"
                                 value="multiple"
                                 checked={tip === "multiple"}
                                 onChange={(e) => setTip(e.target.value)}
                              />
                              Mai mulți
                           </label>
                           <label>
                              <input
                                 type="radio"
                                 name="auto-tip"
                                 value="single"
                                 checked={tip === "single"}
                                 onChange={(e) => setTip(e.target.value)}
                              />
                              Unul
                           </label>
                        </div>
                     </div>

                     <div className="studentAddReservation__add-btns ">
                        <button
                           onClick={handleContinue}
                           disabled={loading || remainingTo30 <= 0}
                           className="studentAddReservation__add-btn studentAddReservation__add-btn--arrow studentAddReservation__add-btn--full"
                           type="button"
                        >
                           <span>
                              {loading ? "Se pregătește..." : "Continuă"}
                           </span>
                           <ReactSVG
                              src={arrowIcon}
                              className="studentAddReservation__add-btn-icon"
                           />
                        </button>
                     </div>
                  </>
               ) : (
                  <>
                     <div className="studentAddReservation__pick-topbar">
                        <button
                           onClick={handleTopbarPrimaryAction}
                           className="studentAddReservation__add-btn studentAddReservation__add-btn--back"
                           type="button"
                           title={
                              showList
                                 ? "Înapoi la calendar"
                                 : "Înapoi la criterii"
                           }
                        >
                           <ReactSVG
                              src={arrowIcon}
                              className="studentAddReservation__add-btn-icon"
                           />
                           <span>
                              {showList ? "Înapoi" : "Înapoi la criterii"}
                           </span>
                        </button>

                        <button
                           type="button"
                           className="studentAddReservation__add-btn studentAddReservation__add-btn--list"
                           onClick={handleTopbarSecondaryAction}
                           title={
                              showList
                                 ? "Înapoi la calendar"
                                 : "Vezi lista selectată"
                           }
                        >
                           <span>
                              {showList
                                 ? "Selecție"
                                 : `Lista (${totalPickedCount})`}
                           </span>
                        </button>
                     </div>

                     {needsPoligonTabs ? (
                        <div className="studentAddReservation__phase-tabs">
                           {poligonNeeded > 0 ? (
                              <button
                                 type="button"
                                 className={`studentAddReservation__phase-tab ${
                                    pickTab === "poligon"
                                       ? "studentAddReservation__phase-tab--active"
                                       : ""
                                 } ${
                                    poligonComplete
                                       ? "studentAddReservation__phase-tab--locked"
                                       : ""
                                 }`}
                                 disabled={poligonComplete}
                                 title={
                                    poligonComplete
                                       ? "Poligon finalizat — continuă pe Oraș, apoi trimite"
                                       : `Lecții pe poligon (${polygonLabel})`
                                 }
                                 onClick={() => {
                                    if (poligonComplete) return;
                                    setPickTab("poligon");
                                    setData(null);
                                    setOraSelectata(null);
                                    setShowList(false);
                                 }}
                              >
                                 Poligon {poligonSelected.length}/
                                 {poligonNeeded}
                              </button>
                           ) : null}
                           <button
                              type="button"
                              className={`studentAddReservation__phase-tab ${
                                 pickTab === "city" || poligonNeeded <= 0
                                    ? "studentAddReservation__phase-tab--active"
                                    : ""
                              } ${
                                 !cityUnlocked
                                    ? "studentAddReservation__phase-tab--locked"
                                    : ""
                              }`}
                              disabled={!cityUnlocked}
                              title={
                                 cityUnlocked
                                    ? `Lecții pe ${sector}`
                                    : `Finalizează poligonul (${poligonSelected.length}/${poligonNeeded})`
                              }
                              onClick={() => {
                                 if (!cityUnlocked) return;
                                 setPickTab("city");
                                 setData(null);
                                 setOraSelectata(null);
                                 setShowList(false);
                              }}
                           >
                              Oraș {citySelected.length}/{cityNeeded}
                           </button>
                        </div>
                     ) : null}

                     {needsPoligonTabs && !showList ? (
                        <div className="studentAddReservation__box">
                           <div className="studentAddReservation__info">
                              {pickTab === "poligon" ? (
                                 <>
                                    Alege orele pentru <b>poligon</b>.
                                 </>
                              ) : (
                                 <>
                                    Alege orele pentru <b>oraș</b>, apoi
                                    trimite.
                                 </>
                              )}
                           </div>
                        </div>
                     ) : null}

                     {!needsPoligonTabs &&
                     requiresPoligon &&
                     remainingPoligon > 0 ? (
                        <div className="studentAddReservation__box">
                           <div className="studentAddReservation__info">
                              Primele {remainingPoligon} lecții din selecție
                              (în ordine cronologică) vor fi pe poligon (
                              {polygonLabel}), restul pe {sector}.
                           </div>
                        </div>
                     ) : null}

                     {!showList ? (
                        <>
                           <div className="studentAddReservation__selector">
                              <div className="studentAddReservation__calendar">
                                 <h3 className="studentAddReservation__title">
                                    {needsPoligonTabs
                                       ? pickTab === "poligon"
                                          ? "Selectează data și ora — Poligon:"
                                          : `Selectează data și ora — Oraș (${sector}):`
                                       : "Selectează data și ora (3 luni):"}
                                 </h3>

                                 <DatePicker
                                    key={`pick-cal-${pickTab}-${sector}`}
                                    selected={data}
                                    onChange={(date) => {
                                       setData(date);
                                       setOraSelectata(null);
                                    }}
                                    inline
                                    locale="ro"
                                    formatWeekDay={(name) =>
                                       name
                                          .substring(0, 2)
                                          .replace(/^./, (c) => c.toUpperCase())
                                    }
                                    minDate={minSelectableDate}
                                    maxDate={maxSelectableDate}
                                    filterDate={isDaySelectable}
                                    dayClassName={(date) => {
                                       const day = calendarDayStr(date);
                                       const classes = [];
                                       if (!isDaySelectable(date)) {
                                          classes.push(
                                             "studentAddReservation__day--inactive",
                                          );
                                       }
                                       if (
                                          existingBookedDaySet.has(day) ||
                                          selectedBookedDaySet.has(day)
                                       ) {
                                          classes.push(
                                             "studentAddReservation__day--mine",
                                          );
                                       }
                                       return classes.join(" ");
                                    }}
                                 />
                              </div>

                              <div className="studentAddReservation__times">
                                 <h3 className="studentAddReservation__title studentAddReservation__times-title">
                                    Selectează:
                                 </h3>

                                 <div className="studentAddReservation__times-list">
                                    {!data && (
                                       <div className="studentAddReservation__disclaimer">
                                          Te rog să selectezi mai întâi o zi!
                                       </div>
                                    )}

                                    {data && dayIsBooked && (
                                       <div className="studentAddReservation__disclaimer">
                                          Ai deja o programare în această zi.
                                          Alege altă zi.
                                       </div>
                                    )}

                                    {oreDisponibile.map((ora) => {
                                       const key =
                                          data && dayLocal
                                             ? availabilitySlotKey(
                                                  dayLocal,
                                                  ora.oraStart,
                                               )
                                             : null;
                                       const candidateIso =
                                          data && dayLocal
                                             ? buildIsoForLocalDayTime(
                                                  dayLocal,
                                                  ora.oraStart,
                                               )
                                             : null;
                                       const alreadyPicked = key
                                          ? selectedKeySet.has(key)
                                          : false;
                                       const isSelected =
                                          oraSelectata?.eticheta ===
                                          ora.eticheta;
                                       const unavailable =
                                          !data ||
                                          !key ||
                                          !candidateIso ||
                                          dayIsBooked ||
                                          alreadyPicked ||
                                          tabReachedLimit ||
                                          reachedPackLimit ||
                                          isSlotDisabledForKey(
                                             key,
                                             candidateIso,
                                          );

                                       const title = getSlotAvailabilityTitle(
                                          key,
                                          candidateIso,
                                          {
                                             alreadyPicked,
                                             dayBooked: dayIsBooked,
                                          },
                                       );

                                       return (
                                          <div
                                             key={ora.eticheta}
                                             className="studentAddReservation__time-slot"
                                          >
                                             <button
                                                onClick={() =>
                                                   setOraSelectata(ora)
                                                }
                                                disabled={unavailable}
                                                className={`studentAddReservation__time-btn ${
                                                   isSelected
                                                      ? "studentAddReservation__time-btn--selected"
                                                      : ""
                                                } ${
                                                   unavailable
                                                      ? "studentAddReservation__time-btn--disabled"
                                                      : ""
                                                }`}
                                                title={title}
                                                type="button"
                                             >
                                                {ora.eticheta}
                                             </button>
                                          </div>
                                       );
                                    })}
                                 </div>
                              </div>
                           </div>

                           <div
                              className={`studentAddReservation__add-btns ${
                                 canSubmitInPick
                                    ? "studentAddReservation__add-btns--dual"
                                    : "studentAddReservation__add-btns--single"
                              }`}
                           >
                              <button
                                 onClick={adaugaProgramare}
                                 disabled={
                                    !data ||
                                    !oraSelectata ||
                                    reachedPackLimit ||
                                    dayIsBooked
                                 }
                                 className={`studentAddReservation__add-btn ${
                                    canSubmitInPick
                                       ? ""
                                       : "studentAddReservation__add-btn--full"
                                 }`}
                                 type="button"
                              >
                                 <ReactSVG
                                    src={addIcon}
                                    className="studentAddReservation__add-btn-icon"
                                 />
                                 <span>Adaugă</span>
                              </button>

                              {canSubmitInPick && (
                                 <button
                                    onClick={trimiteProgramari}
                                    disabled={
                                       loading || requiredSubmitCount <= 0
                                    }
                                    className="studentAddReservation__add-btn studentAddReservation__add-btn--arrow"
                                    type="button"
                                 >
                                    <span>
                                       {loading
                                          ? "Se trimit..."
                                          : `Trimite ${selectedDates.length} prog.`}
                                    </span>
                                    <ReactSVG
                                       src={arrowIcon}
                                       className="studentAddReservation__add-btn-icon"
                                    />
                                 </button>
                              )}
                           </div>
                        </>
                     ) : (
                        <>
                           <div className="studentAddReservation__selector studentAddReservation__selector--col">
                              <div className="studentAddReservation__header-row">
                                 <h3 className="studentAddReservation__title studentAddReservation__title--mt-12">
                                    Lecții selectate: {totalPickedCount} /{" "}
                                    {requiredSubmitCount}
                                    {needsPoligonTabs ? (
                                       <span className="studentAddReservation__muted-note">
                                          {" "}
                                          · poligon {poligonSelected.length}/
                                          {poligonNeeded} · {sector}{" "}
                                          {citySelected.length}/{cityNeeded}
                                       </span>
                                    ) : null}
                                    {rejected.length > 0 && (
                                       <span className="studentAddReservation__muted-note">
                                          {" "}
                                          · conflicte: {rejected.length}
                                       </span>
                                    )}
                                 </h3>
                              </div>

                              <div className="studentAddReservation__added">
                                 {totalPickedCount === 0 ? (
                                    <div className="studentAddReservation__empty-note">
                                       Nu ai selectat încă nicio lecție.
                                    </div>
                                 ) : (
                                    <ul className="studentAddReservation__added-list">
                                       {rejected.map((x, i) => {
                                          const iso = x.iso;
                                          const key = makeKey(iso, i);
                                          const isConfirming =
                                             confirmDeleteKey === key;

                                          return (
                                             <li
                                                key={`r-${i}`}
                                                className={
                                                   "studentAddReservation__added-item studentAddReservation__added-item--conflict" +
                                                   (isConfirming
                                                      ? " studentAddReservation__added-item--confirm"
                                                      : "")
                                                }
                                             >
                                                {!isConfirming ? (
                                                   <>
                                                      <div className="studentAddReservation__added-item-top">
                                                         <time
                                                            className="studentAddReservation__added-item-date"
                                                            dateTime={iso}
                                                         >
                                                            {formatDateRO(iso)}
                                                         </time>
                                                         <span className="studentAddReservation__added-item-time">
                                                            {formatTimeRO(iso)}
                                                         </span>
                                                         <span className="studentAddReservation__added-item-reason">
                                                            {asStr(x.reason) ||
                                                               "Slot respins de server. Alege alt interval."}
                                                         </span>
                                                      </div>
                                                      <ReactSVG
                                                         onClick={() =>
                                                            setConfirmDeleteKey(
                                                               key,
                                                            )
                                                         }
                                                         role="button"
                                                         tabIndex={0}
                                                         focusable="true"
                                                         aria-label="Șterge conflictul"
                                                         src={trashIcon}
                                                         className="studentAddReservation__add-btn-icon studentAddReservation__add-btn-icon--delete"
                                                      />
                                                   </>
                                                ) : (
                                                   <div className="studentAddReservation__confirm">
                                                      <button
                                                         type="button"
                                                         className="studentAddReservation__confirm-btn studentAddReservation__confirm-btn--danger"
                                                         onClick={() =>
                                                            stergeRejected(iso)
                                                         }
                                                      >
                                                         Șterge
                                                      </button>
                                                      <button
                                                         type="button"
                                                         className="studentAddReservation__confirm-btn studentAddReservation__confirm-btn--secondary"
                                                         onClick={() =>
                                                            setConfirmDeleteKey(
                                                               null,
                                                            )
                                                         }
                                                      >
                                                         Renunță
                                                      </button>
                                                   </div>
                                                )}
                                             </li>
                                          );
                                       })}

                                       {selectedDates.map((iso, i) => {
                                          const key = makeKey(iso, i);
                                          const isConfirming =
                                             confirmDeleteKey === key;
                                          const lessonIsPoligon =
                                             needsPoligonTabs &&
                                             poligonSelected.includes(iso);
                                          const lessonSector = lessonIsPoligon
                                             ? polygonLabel
                                             : sector;
                                          const lessonSectorLabel =
                                             needsPoligonTabs
                                                ? lessonIsPoligon
                                                   ? `Poligon`
                                                   : `Oraș`
                                                : sector;

                                          return (
                                             <li
                                                key={`s-${i}`}
                                                className={
                                                   "studentAddReservation__added-item" +
                                                   (isConfirming
                                                      ? " studentAddReservation__added-item--confirm"
                                                      : "")
                                                }
                                             >
                                                {!isConfirming ? (
                                                   <>
                                                      <div className="studentAddReservation__added-item-top">
                                                         <time
                                                            className="studentAddReservation__added-item-date"
                                                            dateTime={iso}
                                                         >
                                                            {formatDateRO(iso)}
                                                         </time>
                                                         <span className="studentAddReservation__added-item-time">
                                                            {formatTimeRO(iso)}
                                                            {' '}
                                                            {lessonSectorLabel}
                                                         </span>
                                                         
                                                      </div>
                                                      <ReactSVG
                                                         onClick={() =>
                                                            setConfirmDeleteKey(
                                                               key,
                                                            )
                                                         }
                                                         role="button"
                                                         tabIndex={0}
                                                         focusable="true"
                                                         aria-label="Șterge programarea"
                                                         src={trashIcon}
                                                         className="studentAddReservation__add-btn-icon studentAddReservation__add-btn-icon--delete"
                                                      />
                                                   </>
                                                ) : (
                                                   <div className="studentAddReservation__confirm">
                                                      <button
                                                         type="button"
                                                         className="studentAddReservation__confirm-btn studentAddReservation__confirm-btn--danger"
                                                         onClick={() =>
                                                            stergeSelected(iso)
                                                         }
                                                      >
                                                         Șterge
                                                      </button>
                                                      <button
                                                         type="button"
                                                         className="studentAddReservation__confirm-btn studentAddReservation__confirm-btn--secondary"
                                                         onClick={() =>
                                                            setConfirmDeleteKey(
                                                               null,
                                                            )
                                                         }
                                                      >
                                                         Renunță
                                                      </button>
                                                   </div>
                                                )}
                                             </li>
                                          );
                                       })}
                                    </ul>
                                 )}
                              </div>

                              {canSubmitInPick && (
                                 <div className="studentAddReservation__add-btns studentAddReservation__add-btns--single">
                                    <button
                                       onClick={trimiteProgramari}
                                       disabled={
                                          loading || requiredSubmitCount <= 0
                                       }
                                       className="studentAddReservation__add-btn studentAddReservation__add-btn--arrow studentAddReservation__add-btn--full"
                                       type="button"
                                    >
                                       <span>
                                          {loading
                                             ? "Se trimit..."
                                             : `Trimite ${selectedDates.length} prog.`}
                                       </span>
                                       <ReactSVG
                                          src={arrowIcon}
                                          className="studentAddReservation__add-btn-icon"
                                       />
                                    </button>
                                 </div>
                              )}
                           </div>
                        </>
                     )}
                  </>
               )}
            </div>

            {showExitPrompt && (
               <div
                  className="studentAddReservation__exit-overlay"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="studentAddReservation-auto-exit-title"
               >
                  <div className="studentAddReservation__exit-modal">
                     <h4
                        id="studentAddReservation-auto-exit-title"
                        className="studentAddReservation__exit-title"
                     >
                        {exitPromptTitle}
                     </h4>
                     <p className="studentAddReservation__exit-text">
                        {exitPromptText}
                     </p>
                     <div className="studentAddReservation__exit-actions">
                        <button
                           type="button"
                           className="studentAddReservation__exit-btn studentAddReservation__exit-btn--stay"
                           onClick={() => setShowExitPrompt(false)}
                        >
                           {exitPromptCancelLabel}
                        </button>
                        <button
                           type="button"
                           className="studentAddReservation__exit-btn studentAddReservation__exit-btn--leave"
                           onClick={confirmExitPrompt}
                        >
                           {exitPromptConfirmLabel}
                        </button>
                     </div>
                  </div>
               </div>
            )}
         </div>
      </>
   );
}
